import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Switch } from '@zond/ui';
import { Loader2, Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getIntegrationDescription,
  getIntegrationLogoUrl,
} from '../../../integrations/constants/integration-metadata';
import { useGetIntegrationProjects } from '../../../integrations/integrations.queries';

interface IntegrationProject {
  id: string;
  name: string;
  key?: string;
  description?: string;
}

interface IntegrationCardProps {
  integration: any;
  selectedMappings: Array<{ integrationType: string; externalId: string; metadata?: any }>;
  onToggleProject: (externalId: string, projectData?: IntegrationProject) => void;
}

export function IntegrationCard({ integration, selectedMappings, onToggleProject }: IntegrationCardProps) {
  const { t } = useTranslation('projects');
  const { data: projects, isLoading } = useGetIntegrationProjects(integration.integrationType, true);
  const [imageError, setImageError] = useState(false);
  const [filterText, setFilterText] = useState('');
  const description = getIntegrationDescription(integration.integrationType);
  const logoUrl = getIntegrationLogoUrl(integration.integrationType);

  const getIntegrationLabel = (type: string) => {
    switch (type) {
      case 'GitHub':
        return t('integrations.labels.repositories');
      case 'ClickUp':
        return t('integrations.labels.spaces');
      case 'Slack':
        return t('integrations.labels.channels');
      default:
        return t('integrations.labels.projects');
    }
  };

  const isProjectSelected = (projectId: string) => {
    return selectedMappings.some((m) => m.externalId === projectId);
  };

  const filteredProjects = projects?.filter((project: IntegrationProject) => {
    if (!filterText) return true;
    const searchLower = filterText.toLowerCase();
    return (
      project.name.toLowerCase().includes(searchLower) ||
      project.key?.toLowerCase().includes(searchLower) ||
      project.description?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-4">
          <div className="flex size-10 items-center justify-center rounded-lg bg-muted p-2">
            {imageError ? (
              <div className="text-sm font-medium">
                {integration.integrationType.slice(0, 2).toUpperCase()}
              </div>
            ) : (
              <img
                src={logoUrl}
                alt={t('integrations.logoAlt', { name: integration.integrationType })}
                className="size-full object-contain"
                onError={() => setImageError(true)}
              />
            )}
          </div>
          <div className="flex-1">
            <CardTitle className="mb-1">{integration.integrationType}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            {t('integrations.loading', {
              items: getIntegrationLabel(integration.integrationType).toLowerCase(),
            })}
          </div>
        ) : projects && projects.length > 0 ? (
          <div className="space-y-1">
            <div className="text-sm font-medium text-muted-foreground mb-3">
              {t('integrations.available', {
                items: getIntegrationLabel(integration.integrationType).toLowerCase(),
              })}
            </div>
            <div className="relative ">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('integrations.searchPlaceholder', {
                  items: getIntegrationLabel(integration.integrationType).toLowerCase(),
                })}
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="pl-9"
              />
            </div>
            {filteredProjects && filteredProjects.length > 0 ? (
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar mt-[15px]">
                {filteredProjects.map((project: IntegrationProject) => (
                  <div
                    key={project.id}
                    className="flex items-center gap-3 p-3 rounded-lg border hover:bg-accent transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">{project.name}</p>
                        {project.key && (
                          <Badge variant="outline" className="text-xs shrink-0">
                            {project.key}
                          </Badge>
                        )}
                      </div>
                      {project.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {project.description}
                        </p>
                      )}
                    </div>
                    <Switch
                      checked={isProjectSelected(project.id)}
                      onCheckedChange={() => onToggleProject(project.id, project)}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>
                  {t('integrations.noMatch', {
                    items: getIntegrationLabel(integration.integrationType).toLowerCase(),
                  })}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <p>
              {t('integrations.noneAvailable', {
                items: getIntegrationLabel(integration.integrationType).toLowerCase(),
              })}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
